/**
 * NFT Rarity System
 * Mirrors backend logic for deterministic trait generation
 * Only INLAYS have rarity
 */

// Only inlays have rarity
export type Rarity = 'rare' | 'epic' | 'legendary';

export const RARITY_ORDER: Rarity[] = ['rare', 'epic', 'legendary'];

// Drop rates for regular numbers (cumulative thresholds)
// Rare: 70%, Epic: 25%, Legendary: 5%
const REGULAR_DROP_THRESHOLDS: Record<Rarity, number> = {
  rare: 0.70,
  epic: 0.95,
  legendary: 1.0,
};

// Drop rates for beautiful numbers - shifted toward rarer traits
// Rare: 40%, Epic: 40%, Legendary: 20%
const BEAUTIFUL_DROP_THRESHOLDS: Record<Rarity, number> = {
  rare: 0.40,
  epic: 0.80,
  legendary: 1.0,
};

// ============================================
// BACKGROUNDS (12) - gradient pairs: from → to
// ============================================
export interface BackgroundDefinition {
  name: string;
  from: string;
  to: string;
}

export const BACKGROUNDS: BackgroundDefinition[] = [
  { name: 'sunset', from: '#FBE776', to: '#D13E4A' },
  { name: 'ocean', from: '#6CA4F7', to: '#205FBD' },
  { name: 'mint', from: '#7CE7AA', to: '#33BD6F' },
  { name: 'olive', from: '#7E977E', to: '#476047' },
  { name: 'coral', from: '#CE7979', to: '#9C403F' },
  { name: 'gold', from: '#FBE776', to: '#D13E4A' },
  { name: 'lavender', from: '#B492BF', to: '#896794' },
  { name: 'slate', from: '#515D7C', to: '#323A51' },
  { name: 'rose', from: '#E08AAB', to: '#AF456D' },
  { name: 'sky', from: '#B7C0F7', to: '#6875C7' },
  { name: 'sand', from: '#A9867E', to: '#755048' },
  { name: 'violet', from: '#CFAFF8', to: '#8C68BB' },
];

// ============================================
// PATTERNS (31) - background patterns
// ============================================
export const PATTERNS: string[] = [
  'aesty', 'basketball', 'bike', 'bonjour', 'cap', 'cinema', 'controller',
  'cup', 'cutlery', 'esimrwa', 'eyes', 'flame', 'inkly', 'kitty', 'love',
  'mail', 'microphone', 'moon', 'phone', 'piano', 'plane', 'present',
  'safari', 'sport', 'swirl', 'teleplane', 'train', 'umbrella', 'vocapp',
  'waves', 'world',
];

// ============================================
// BASE (7) - chip base colors
// ============================================
export const BASES: string[] = [
  'blue', 'green', 'orange', 'pink', 'purple', 'red', 'yellow',
];

// ============================================
// INLAYS (8) - with rarity!
// ============================================
export interface InlayDefinition {
  name: string;
  rarity: Rarity;
}

export const INLAYS: InlayDefinition[] = [
  // Rare (5)
  { name: 'silver', rarity: 'rare' },
  { name: 'aquamarine', rarity: 'rare' },
  { name: 'amethyst', rarity: 'rare' },
  { name: 'emerald', rarity: 'rare' },
  { name: 'ruby', rarity: 'rare' },
  // Epic (2)
  { name: 'gold', rarity: 'epic' },
  { name: 'platinum', rarity: 'epic' },
  // Legendary (1)
  { name: 'tanzanite', rarity: 'legendary' },
];

// ============================================
// SYMBOLS (31) - chip symbols
// ============================================
export const SYMBOLS: string[] = [
  'atom', 'basketball', 'bbcourt', 'calibri', 'car', 'checkmark', 'controller',
  'cup', 'doggy', 'eyes', 'fishy', 'gecko', 'gg', 'gsuite', 'hash', 'heart',
  'kitty', 'leaf', 'mic', 'moon', 'optimus', 'paw', 'plane', 'shield',
  'smiley', 'snail', 'soccer', 'star', 'superbowl', 'ton', 'volleyball',
];

// ============================================
// Rarity colors (for inlay display)
// ============================================
export const RARITY_COLORS: Record<Rarity, { text: string; bg: string; glow?: string }> = {
  rare: { text: '#60a5fa', bg: 'rgba(96, 165, 250, 0.15)' },
  epic: { text: '#c084fc', bg: 'rgba(192, 132, 252, 0.15)', glow: '0 0 8px rgba(192, 132, 252, 0.5)' },
  legendary: { text: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)', glow: '0 0 12px rgba(251, 191, 36, 0.5)' },
};

// ============================================
// Beauty score calculation
// ============================================

export function calculateBeautyScore(phoneNumber: string): number {
  const digits = phoneNumber.replace(/\D/g, '');
  const last4 = digits.slice(-4);
  
  if (last4.length < 4) return 0;

  let score = 0;

  // All same digit (e.g., 7777)
  if (new Set(last4).size === 1) score += 100;

  // 3+ consecutive same digits (e.g., 7771, 1777)
  if (/(\d)\1{2,}/.test(last4)) score += 40;

  // Sequential patterns (1234, 4321)
  const isSequential = (s: string, dir: 1 | -1) => {
    for (let i = 1; i < s.length; i++) {
      if (parseInt(s[i]!) !== parseInt(s[i-1]!) + dir) return false;
    }
    return true;
  };

  if (isSequential(last4, 1) || isSequential(last4, -1)) {
    score += 50;
  }

  // Palindrome (e.g., 1221, 7337)
  if (last4 === last4.split('').reverse().join('')) {
    score += 35;
  }

  // Repeating pairs (e.g., 1212, 7878)
  if (/^(\d)(\d)\1\2$/.test(last4)) score += 30;

  // Double pairs (e.g., 1122, 7788)
  if (/^(\d)\1(\d)\2$/.test(last4)) score += 25;

  // Lucky numbers (7, 8)
  const luckyCount = (last4.match(/[78]/g) || []).length;
  score += luckyCount * 5;

  // Low digit diversity (fewer unique = more memorable)
  const uniqueDigits = new Set(last4).size;
  if (uniqueDigits === 1) score += 30;
  else if (uniqueDigits === 2) score += 15;

  return Math.min(score, 100);
}

export function isBeautifulNumber(phoneNumber: string): boolean {
  return calculateBeautyScore(phoneNumber) >= 30;
}

// ============================================
// Hash function (matches backend)
// ============================================

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash;
}

function selectRarity(rand: number, thresholds: Record<Rarity, number>): Rarity {
  for (const rarity of RARITY_ORDER) {
    if (rand < thresholds[rarity]) return rarity;
  }
  return 'rare';
}

function selectInlayByRarity(rarity: Rarity, hash: number): InlayDefinition {
  const inlaysInRarity = INLAYS.filter(i => i.rarity === rarity);
  if (inlaysInRarity.length === 0) {
    const rareInlays = INLAYS.filter(i => i.rarity === 'rare');
    return rareInlays[Math.abs(hash) % rareInlays.length] ?? INLAYS[0]!;
  }
  return inlaysInRarity[Math.abs(hash >> 4) % inlaysInRarity.length]!;
}

// ============================================
// NFT Traits structure
// ============================================

export interface NftTraits {
  background: string;
  backgroundGradient: { from: string; to: string };
  pattern: string;
  base: string;
  inlay: string;
  inlayRarity: Rarity;
  symbol: string;
  beautyScore: number;
  isBeautiful: boolean;
}

/**
 * Generate deterministic traits from phone number
 * Same as backend logic for consistency
 */
export function generateTraits(phoneNumber: string): NftTraits {
  const beautyScore = calculateBeautyScore(phoneNumber);
  const isBeautiful = isBeautifulNumber(phoneNumber);

  // Hash for each trait type
  const hashBg = hashString(phoneNumber + '_background');
  const hashPattern = hashString(phoneNumber + '_pattern');
  const hashBase = hashString(phoneNumber + '_base');
  const hashInlay = hashString(phoneNumber + '_inlay');
  const hashSymbol = hashString(phoneNumber + '_symbol');

  // Select background
  const bgIndex = Math.abs(hashBg) % BACKGROUNDS.length;
  const background = BACKGROUNDS[bgIndex]!;

  // Select pattern
  const patternIndex = Math.abs(hashPattern) % PATTERNS.length;
  const pattern = PATTERNS[patternIndex]!;

  // Select base
  const baseIndex = Math.abs(hashBase) % BASES.length;
  const base = BASES[baseIndex]!;

  // Select inlay with rarity
  const thresholds = isBeautiful ? BEAUTIFUL_DROP_THRESHOLDS : REGULAR_DROP_THRESHOLDS;
  const randInlay = Math.abs(hashInlay % 10000) / 10000;
  const inlayRarity = selectRarity(randInlay, thresholds);
  const inlay = selectInlayByRarity(inlayRarity, hashInlay);

  // Select symbol
  const symbolIndex = Math.abs(hashSymbol) % SYMBOLS.length;
  const symbol = SYMBOLS[symbolIndex]!;

  return {
    background: background.name,
    backgroundGradient: { from: background.from, to: background.to },
    pattern,
    base,
    inlay: inlay.name,
    inlayRarity,
    symbol,
    beautyScore,
    isBeautiful,
  };
}

/**
 * Get rarity label for display
 */
export function getRarityLabel(rarity: Rarity): string {
  const labels: Record<Rarity, string> = {
    rare: 'Редкий',
    epic: 'Эпический',
    legendary: 'Легендарный',
  };
  return labels[rarity];
}

/**
 * Find inlay rarity by name
 */
export function getInlayRarity(inlayName: string): Rarity {
  const found = INLAYS.find(i => i.name === inlayName);
  return found?.rarity ?? 'rare';
}

/**
 * Get background by name
 */
export function getBackground(name: string): BackgroundDefinition | undefined {
  return BACKGROUNDS.find(b => b.name === name);
}
